import { cacheService } from '@/services/cacheService'
import { thumbnailCache } from '@/services/thumbnailCacheService'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useCacheStore = defineStore('cache', () => {
  // État des performances
  const isLoading = ref(false)
  const lastDataUpdate = ref<Date | null>(null)
  const cacheStats = ref({
    data: { totalEntries: 0, validEntries: 0, expiredEntries: 0 },
    thumbnails: {
      totalEntries: 0,
      totalSizeKB: 0,
      totalSizeMB: 0,
      maxSizeMB: 0,
      usage: '0%',
    },
  })

  // États dérivés
  const cacheHealth = computed(() => {
    const data = cacheStats.value.data
    const thumbnails = cacheStats.value.thumbnails

    return {
      dataEfficiency:
        data.totalEntries > 0 ? Math.round((data.validEntries / data.totalEntries) * 100) : 0,
      thumbnailUsage: parseFloat(thumbnails.usage.replace('%', '')),
      isHealthy:
        data.expiredEntries < data.totalEntries * 0.1 && // Moins de 10% d'expirés
        parseFloat(thumbnails.usage.replace('%', '')) < 90, // Moins de 90% d'usage
    }
  })

  const performanceIndicators = computed(() => {
    const health = cacheHealth.value
    return {
      status: health.isHealthy
        ? 'excellent'
        : health.dataEfficiency > 80
          ? 'good'
          : health.dataEfficiency > 50
            ? 'warning'
            : 'poor',
      score: Math.round((health.dataEfficiency + (100 - health.thumbnailUsage)) / 2),
      recommendations: generateRecommendations(health),
    }
  })

  // Actions
  async function updateCacheStats() {
    try {
      cacheStats.value = {
        data: cacheService.getStats(),
        thumbnails: await thumbnailCache.getStats(),
      }
      //console.log('[CacheStore] Stats mises à jour:', cacheStats.value)
    } catch (error) {
      console.error('[CacheStore] Erreur mise à jour stats:', error)
    }
  }

  function generateRecommendations(health: {
    dataEfficiency: number
    thumbnailUsage: number
    isHealthy: boolean
  }): string[] {
    const recommendations: string[] = []

    if (health.dataEfficiency < 50) {
      recommendations.push('Nettoyer le cache des données expirées')
    }

    if (health.thumbnailUsage > 80) {
      recommendations.push('Réduire la taille du cache des miniatures')
    }

    if (!health.isHealthy) {
      recommendations.push('Effectuer un nettoyage complet des caches')
    }

    if (recommendations.length === 0) {
      recommendations.push('Performances optimales')
    }

    return recommendations
  }

  async function clearAllCaches() {
    try {
      isLoading.value = true
      cacheService.invalidateAll()
      thumbnailCache.clear()
      await updateCacheStats()
      console.log('[CacheStore] Tous les caches vidés')
    } catch (error) {
      console.error('[CacheStore] Erreur vidage caches:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function clearDataCache() {
    try {
      cacheService.invalidateAll()
      await updateCacheStats()
      console.log('[CacheStore] Cache des données vidé')
    } catch (error) {
      console.error('[CacheStore] Erreur vidage cache données:', error)
    }
  }

  async function clearThumbnailCache() {
    try {
      thumbnailCache.clear()
      await updateCacheStats()
      console.log('[CacheStore] Cache des miniatures vidé')
    } catch (error) {
      console.error('[CacheStore] Erreur vidage cache miniatures:', error)
    }
  }

  async function optimizeCaches() {
    try {
      isLoading.value = true

      // Nettoyer les caches expirés
      cacheService.cleanup()

      // Laisser le nettoyage automatique des miniatures se faire
      await updateCacheStats()

      console.log('[CacheStore] Optimisation des caches terminée')
    } catch (error) {
      console.error('[CacheStore] Erreur optimisation caches:', error)
    } finally {
      isLoading.value = false
    }
  }

  function markDataUpdate() {
    lastDataUpdate.value = new Date()
  }

  // Actions automatiques
  async function initializeCacheMonitoring() {
    // Mise à jour initiale
    await updateCacheStats()

    // Mise à jour périodique des stats
    setInterval(updateCacheStats, 30 * 1000) // Toutes les 30 secondes

    // Optimisation automatique
    setInterval(optimizeCaches, 5 * 60 * 1000) // Toutes les 5 minutes

    //console.log('[CacheStore] Monitoring des caches initialisé')
  }

  return {
    // État
    isLoading,
    lastDataUpdate,
    cacheStats,

    // États dérivés
    cacheHealth,
    performanceIndicators,

    // Actions
    updateCacheStats,
    clearAllCaches,
    clearDataCache,
    clearThumbnailCache,
    optimizeCaches,
    markDataUpdate,
    initializeCacheMonitoring,
  }
})
